import { html, LitElement } from 'lit-element';
import { isMultiPartMER, latexToHTML } from '../headerBlocks/utils.js';

// return each session as { id, answer, correctness }
const getParsedSessions = (sessionData, item) =>
  (sessionData || []).map(session => {
    const currentItem = ((item && item.models) || []).filter(
      model => model.id === session.id
    );
    const { element, choiceMode, choices, keyMode, responses } = currentItem[0];
    const charCode = keyMode === 'numbers' ? 49 : 65;

    const { value, completeAnswer } = session || {};
    let correct;
    let incorrect;
    let correctness;

    switch (element) {
      case 'multiple-choice':
        // eslint-disable-next-line no-case-declarations
        const values = (choices || []).reduce((acc, choice, index) => {
          if ((value || []).indexOf(choice.value) >= 0) {
            acc.push(index);
          }

          return acc;
        }, []);

        correct = 0;
        incorrect = 0;
        correctness = 'partial';

        values.forEach(index => {
          if (choices[index].correct) {
            correct += 1;
          } else {
            incorrect += 1;
          }
        });
        if (correct === values.length) {
          correctness = 'correct';
        } else if (incorrect === values.length) {
          correctness = 'incorrect';
        }

        if (choiceMode === 'checkbox') {
          return {
            id: session.id,
            answer: values
              .map(index => String.fromCharCode(index + charCode))
              .join(', '),
            correctness: values.length ? correctness : undefined,
          };
        }

        // eslint-disable-next-line no-case-declarations
        let filteredChoice = {};
        (choices || []).forEach((choice, index) => {
          if (choice.value === value[0]) {
            filteredChoice = {
              label: choice.label,
              index,
              correctness: choice.correct ? 'correct' : 'incorrect',
            };
          }
        });

        return {
          id: session.id,
          answer: filteredChoice.label
            ? `${String.fromCharCode(filteredChoice.index + charCode)}: ${
                filteredChoice.label
              }`
            : '',
          correctness: filteredChoice.correctness,
        };

      case 'math-inline':
        correctness = 'incorrect';

        (responses || []).forEach(response => {
          if (response.answer === completeAnswer) {
            correctness = 'correct';
          }
        });

        return {
          id: session.id,
          answer: completeAnswer,
          correctness: completeAnswer ? correctness : undefined,
        };

      case 'explicit-constructed-response':
        // eslint-disable-next-line no-case-declarations
        const areaKeys = Object.keys(choices || {});
        correct = 0;
        incorrect = 0;

        (areaKeys || []).forEach(area => {
          if (
            choices[area] &&
            choices[area].find(ch => ch.label === value[area])
          ) {
            correct += 1;
          } else {
            incorrect += 1;
          }
        });

        correctness = 'partial';

        if (correct === areaKeys.length) {
          correctness = 'correct';
        } else if (incorrect === areaKeys.length) {
          correctness = 'incorrect';
        }
        return {
          id: session.id,
          answer: (areaKeys || [])
            .map(area => value[area] || '_{\\text{-----}}')
            .join(', '),
          correctness,
        };

      default:
        return { id: session.id, answer: '' };
    }
  });

/**
 * @element response-block-multi-item
 * @prop {Object} response - Response
 */
export class ResponseBlockMultiItem extends LitElement {
  static get properties() {
    return {
      response: { type: Object, attribute: false },
      correctnessGroup: { type: String, attribute: false },
    };
  }

  createRenderRoot() {
    return this;
  }

  openModal({ name, avatar }) {
    if (this.correctnessGroup === 'unanswered') {
      return;
    }

    const content = html`<response-modal-multi-item
      .response=${this.response}
      .values=${this.valuesToRender}
    ></response-modal-multi-item>`;

    const title = html`
      <div class="modal-response-header">
        ${avatar}
        <span class="student-name">${name || ''}</span>
      </div>
    `;

    window.dispatchEvent(
      new CustomEvent('classroom-tool-live-open-modal', {
        detail: {
          modalContent: content,
          modalType: 'response-modal-multi-item',
          modalTitle: title,
        },
      })
    );
  }

  render() {
    const { sessionData, item, student } = this.response || {};
    const { name, photo, isOnline } = student || {};
    const online = isOnline || isOnline === undefined;
    const isMER = isMultiPartMER(item);

    const blockClasses =
      this.correctnessGroup && this.correctnessGroup === 'unanswered'
        ? 'block'
        : 'block expandable';

    this.valuesToRender = getParsedSessions(sessionData, item);

    let avatar;
    const opacityClass = !online ? 'offline-opacity' : '';
    const avatarClasses = `block-avatar ${opacityClass}`;

    if (photo) {
      avatar = html`<img class=${avatarClasses} src="${photo}" />`;
    } else {
      const initials = (name || '')
        .split(' ')
        .map(namePart => (namePart ? namePart[0] : ''));

      avatar = html`<div class=${avatarClasses}>${initials}</div>`;
    }

    return html` <div
      class=${online ? blockClasses : `${blockClasses} offline`}
      @click=${() => this.openModal({ name, avatar })}
    >
      ${avatar}
      <div class="block-details">
        <span class=${`student-name ${opacityClass}`}>${name || ''}</span>

        ${(this.valuesToRender || []).map((value, index) => {
          const className = isMER
            ? `${
                value.correctness === 'correct' ? 'correct' : 'incorrect'
              }-answer`
            : '';

          return html` <div class="choice-value">
            <div class="block-details-important">${`${index + 1}.`}</div>
            <div class="${className}">
              ${value && value.answer
                ? html`<math-rendering-element
                    .label="${latexToHTML(value.answer)}"
                  ></math-rendering-element>`
                : html`<div>Not answered</div>`}
            </div>
          </div>`;
        })}
      </div>
      ${!online
        ? html`<div class="offline-label offline-opacity">offline</div>`
        : ''}
    </div>`;
  }
}

export default ResponseBlockMultiItem;
